<template>
  <div>
    <template v-if="!selectGaugesResponse">
      <div>
        <strong> {{ scanBodySelectionLabel?.toUpperCase() }} </strong>
      </div>
      <span class="primary--text">
        Select the <strong> top facet </strong> of each of the
        <strong> {{ scanBodySelectionLabel }} </strong> in each scan to prepare.
      </span>
    </template>
    <span class="primary--text" v-if="selectGaugesResponse">
      Confirm that the Scan Bodies have been selected correctly.
    </span>
    <v-card flat class="d-flex">
      <template v-if="!selectGaugesResponse">
        <v-card-text>
          <strong>Computer</strong>
          <ul>
            <li>Left-click to select.</li>
            <li>Click any facet again to unselect.</li>
            <li>Rotate scan by holding left-click and dragging.</li>
            <li>Use mouse scroll-wheel to zoom.</li>
          </ul>
        </v-card-text>
        <v-card-text>
          <strong>Touch Devices</strong>
          <ul>
            <li>Tap to select.</li>
            <li>Tap again to unselect.</li>
            <li>Hold and drag to rotate scan.</li>
            <li>Pinch to zoom.</li>
          </ul>
        </v-card-text>
      </template>
      <template v-if="selectGaugesResponse">
        <v-card-text>
          <strong>Ready to continue?</strong>
          <ul>
            <li>Click <strong>BEGIN ALIGNMENT</strong>.</li>
          </ul>
        </v-card-text>
        <v-card-text>
          <strong>Made a mistake?</strong>
          <ul>
            <li>
              Click
              <strong> RE-SELECT SCAN BODIES </strong>
              to try again.
            </li>
          </ul>
        </v-card-text>
      </template>
    </v-card>
    <div class="d-flex">
      <v-row>
        <v-col cols="12">
          <template v-if="!selectGaugesResponse">
            <v-btn
              class="elevation-0 white--text text-body-2 font-weight-medium btn-primary mr-3"
              @click="select"
              :disabled="loading"
            >
              Confirm {{ scanBodySelectionLabel }}
            </v-btn>
            <v-btn
              class="elevation-0 rounded-10 text-body-2 font-weight-medium"
              @click="clearSelections"
              :disabled="loading"
            >
              Clear All Selections
            </v-btn>
          </template>
          <template v-if="selectGaugesResponse">
            <v-btn
              class="elevation-0 white--text text-body-2 font-weight-medium rounded-10 elevation-0 warning mr-3"
              @click="align"
              :disabled="loading"
            >
              Begin Alignment
            </v-btn>
            <v-btn
              class="elevation-0 rounded-10 text-body-2 font-weight-medium"
              @click="reselect"
              :disabled="loading"
            >
              Re-Select {{ scanBodySelectionLabel }}
            </v-btn>
          </template>
        </v-col>
        <v-col class="pt-0">
          <v-scale-transition origin="center center">
            <div v-if="loading">
              <v-progress-linear
                rounded
                indeterminate
                color="primary"
                height="36"
                class="rounded-10"
                background-opacity="0.6"
              >
                <span
                  class="text-body-2 font-weight-medium white--text loading-pulse"
                >
                  PROCESSING...
                </span>
              </v-progress-linear>
            </div>
          </v-scale-transition>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import { toTitleCase } from "@/utils"
import * as NCFrontend from "@osteon-nexus-connect-lib/nc-frontend"
import apiClient from "@/lib/ApiClient"

export default {
  name: "PreAlignment",

  props: ["dentate"],

  scan1View: null,
  scan2View: null,

  data() {
    return {
      loading: false,
      selectGaugesResponse: null
    }
  },

  computed: {
    ...mapGetters([
      "accessToken",
      "retrying",
      "orderDetails",
      "upperAddScansResponse",
      "lowerAddScansResponse",
      "faceCount",
      "narrowScanBodiesPresent",
      "scanBodySelectionLabel",
      "upperValidationOutcome",
      "lowerValidationOutcome"
    ]),

    addScansResponse() {
      return this[`${this.dentate}AddScansResponse`]
    }
  },

  methods: {
    ...mapActions([
      "setUpperValidateScansResponse",
      "setLowerValidateScansResponse",
      "setScanBodySelectionLabel",
      "setPageState",
      "setTransition",
      "setRetrying",
      "setFaceCount",
      "setErrorMessage"
    ]),

    async select() {
      this.scan1View = NCFrontend.get("scan1View")
      this.scan2View = NCFrontend.get("scan2View")

      const scan1Visualisation = this.scan1View.visualisation
      const scan2Visualisation = this.scan2View.visualisation

      const scan1SelectedPlaneIndicesForScanGauges =
        scan1Visualisation.selectedPlaneIndicesForScanGauges
      const scan2SelectedPlaneIndicesForScanGauges =
        scan2Visualisation.selectedPlaneIndicesForScanGauges

      if (
        scan1SelectedPlaneIndicesForScanGauges.length === 0 ||
        scan2SelectedPlaneIndicesForScanGauges.length === 0
      ) {
        this.setErrorMessage("No Scan Gauges have been selected.")
        return
      }
      if (
        scan1SelectedPlaneIndicesForScanGauges.length !==
        scan2SelectedPlaneIndicesForScanGauges.length
      ) {
        this.setErrorMessage("Mismatching number of Scan Gauges selected.")
        return
      }

      let scan1SelectedPlaneIndicesForNarrowScanBodies = []
      let scan2SelectedPlaneIndicesForNarrowScanBodies = []
      if (this.narrowScanBodiesPresent) {
        if (
          scan1Visualisation.selectingNarrowScanBodies &&
          scan2Visualisation.selectingNarrowScanBodies
        ) {
          scan1SelectedPlaneIndicesForNarrowScanBodies =
            scan1Visualisation.selectedPlaneIndicesForNarrowScanBodies
          scan2SelectedPlaneIndicesForNarrowScanBodies =
            scan2Visualisation.selectedPlaneIndicesForNarrowScanBodies
          if (
            scan1SelectedPlaneIndicesForNarrowScanBodies.length === 0 ||
            scan2SelectedPlaneIndicesForNarrowScanBodies.length === 0
          ) {
            this.setErrorMessage("No Narrow Scan Bodies have been selected.")
            return
          }
          if (
            scan1SelectedPlaneIndicesForNarrowScanBodies.length !==
            scan2SelectedPlaneIndicesForNarrowScanBodies.length
          ) {
            this.setErrorMessage(
              "Mismatching number of Narrow Scan Bodies selected."
            )
            return
          }
        } else {
          scan1Visualisation.startSelectionForNarrowScanBodies()
          scan2Visualisation.startSelectionForNarrowScanBodies()
          this.setScanBodySelectionLabel("Narrow Scan Bodies")
          return
        }
      }

      if (
        this.faceCount !== null &&
        scan1SelectedPlaneIndicesForScanGauges.length +
          scan1SelectedPlaneIndicesForNarrowScanBodies.length !==
          this.faceCount
      ) {
        this.setErrorMessage(
          `Selection Mismatch: Must select ${this.faceCount} Scan Gauges`
        )
        return
      }

      try {
        this.loading = true
        scan1Visualisation.stopSelection()
        scan2Visualisation.stopSelection()
        const selectGaugesResponse =
          await NCFrontend.ApiClient.sessionSelectGauges(
            `${apiClient.instanceUrl}/${apiClient.tenantUid}/validation`,
            `Bearer ${this.accessToken}`,
            {
              sessionId:
                this.orderDetails[`${this.dentate}_options`].nc_session_id,
              scan1Id: this.addScansResponse.scan1.id,
              scan2Id: this.addScansResponse.scan2.id,
              scan1SelectedPlaneIndicesForScanGauges,
              scan1SelectedPlaneIndicesForNarrowScanBodies,
              scan2SelectedPlaneIndicesForScanGauges,
              scan2SelectedPlaneIndicesForNarrowScanBodies
            }
          )

        await Promise.all([
          scan1Visualisation.showSelectedScanBodies(
            selectGaugesResponse.scan1.scanGauges,
            selectGaugesResponse.scan1.narrowScanBodies
          ),
          scan2Visualisation.showSelectedScanBodies(
            selectGaugesResponse.scan2.scanGauges,
            selectGaugesResponse.scan2.narrowScanBodies
          )
        ])
        this.setScanBodySelectionLabel(null)
        this.selectGaugesResponse = selectGaugesResponse
      } catch (err) {
        this.setErrorMessage("An internal error occurred.")
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    async align() {
      try {
        this.loading = true
        this[`set${toTitleCase(this.dentate)}ValidateScansResponse`](
          await NCFrontend.ApiClient.sessionValidateScans(
            `${apiClient.instanceUrl}/${apiClient.tenantUid}/validation`,
            `Bearer ${this.accessToken}`,
            {
              sessionId:
                this.orderDetails[`${this.dentate}_options`].nc_session_id,
              scan1Id: this.addScansResponse.scan1.id,
              scan2Id: this.addScansResponse.scan2.id,
              retrying: this.retrying
            }
          )
        )
        this.setTransition(true)
        if (this[`${this.dentate}ValidationOutcome`] !== "Pass") {
          if (this.$vuetify.breakpoint.lgAndUp) {
            this.setPageState([
              { cols: 3, component: "SuggestedSolutions" },
              { cols: 5, component: "ValidatedDiv", prop: this.dentate }
            ])
          } else {
            this.setPageState([
              { cols: 5, component: "ValidatedDiv", prop: this.dentate }
            ])
          }
        } else
          this.setPageState([
            {
              cols: this.$vuetify.breakpoint.lgAndUp ? 7 : 5,
              component: "ValidatedDiv",
              prop: this.dentate
            }
          ])

        if (this.faceCount === null) {
          const visualisation1 = NCFrontend.get("scan1View").visualisation
          const gaugeCount =
            visualisation1.selectedPlaneIndicesForScanGauges?.length || 0
          const narrowCount =
            visualisation1.scan1SelectedPlaneIndicesForNarrowScanBodies
              ?.length || 0

          this.setFaceCount(gaugeCount + narrowCount)
        }

        this.setRetrying(false)

        this.$emit("switch")
      } catch (err) {
        this.setErrorMessage("An internal error occurred.")
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    reselect() {
      NCFrontend.get("scan1View").visualisation.reset()
      NCFrontend.get("scan2View").visualisation.reset()
      NCFrontend.get("scan1View").visualisation.startSelectionForScanGauges()
      NCFrontend.get("scan2View").visualisation.startSelectionForScanGauges()
      this.setScanBodySelectionLabel("Scan Gauges")
      this.selectGaugesResponse = null
    },

    clearSelections() {
      NCFrontend.get("scan1View").visualisation.clearSelections()
      NCFrontend.get("scan2View").visualisation.clearSelections()

      NCFrontend.get("scan1View").visualisation.startSelectionForScanGauges()
      NCFrontend.get("scan2View").visualisation.startSelectionForScanGauges()

      NCFrontend.get("scan1View").visualisation.clearSelections()
      NCFrontend.get("scan2View").visualisation.clearSelections()

      this.setScanBodySelectionLabel("Scan Gauges")
    }
  }
}
</script>
